/**
 * 教材数据源（DataSource）
 * ------------------------------------------------------------
 * 系统里「教材内容」有两个来源：
 *   1) data/textbooks/ 下的静态文件（内置教材，随项目一起部署）
 *   2) 用户在「导入中心」确认后写入的存储数据
 *
 * 统一在这里合并成一个视图。新增教材只需要放文件 + 改 catalog.json，
 * 不需要修改任何程序逻辑。
 *
 * 加载策略：
 *   - 启动时读取 catalog.json，把每个教材的 index.json 加载进来（教材级元数据）
 *   - 单元内容（单词/句子/语法/题目）按需懒加载并缓存在内存，避免一次性拉全部数据
 */
import { getStorage } from '../core/storage/index.js';
import { COLLECTIONS } from '../core/schema.js';
import { attachQuestionRefs } from './knowledge.js';

const cache = {
  catalogLoaded: false,
  textbooks: new Map(),      // id -> textbook meta（含 units 索引）
  unitContent: new Map(),    // textbookId::unitId -> { words, sentences, grammar, questions }
};

/** 数据根路径：兼容部署在子目录（vite base='./'）的情况 */
const BASE = new URL('data/textbooks/', document.baseURI).href.replace(/\/$/, '');

/**
 * 读取 JSON。
 * 优先读 window.__INLINE_DATA__（单文件离线版把教材 JSON 全部内联在 HTML 里，
 * 这样双击本地 HTML 也能用，不受 fetch 的 file:// 限制）；
 * 否则按路径请求静态文件（常规部署方式）。
 */
async function fetchJSON(path) {
  const inline = typeof window !== 'undefined' ? window.__INLINE_DATA__ : null;
  if (inline) {
    const key = `data/textbooks/${path.split('data/textbooks/').pop()}`;
    if (inline[key]) return JSON.parse(JSON.stringify(inline[key]));
  }
  const res = await fetch(path, { cache: 'no-cache' });
  if (!res.ok) throw new Error(`加载失败 ${path}（${res.status}）`);
  return res.json();
}

/** 加载内置教材目录（仅教材级元数据，不含单元内容） */
export async function loadCatalog() {
  if (cache.catalogLoaded) return [...cache.textbooks.values()];
  const catalog = await fetchJSON(`${BASE}/catalog.json`);
  for (const entry of catalog.textbooks || []) {
    try {
      const index = await fetchJSON(`${BASE}/${entry.dir}/index.json`);
      cache.textbooks.set(index.id, {
        ...index,
        source: 'file',
        dir: entry.dir,
        // units 里带 file 字段，按需加载
        unitIndex: index.units || [],
      });
    } catch (err) {
      console.warn('[datasource] 教材加载失败', entry, err);
    }
  }
  cache.catalogLoaded = true;
  return [...cache.textbooks.values()];
}

/** 从静态文件读取某单元内容 */
async function loadUnitFromFile(textbookId, unitId) {
  const tb = cache.textbooks.get(textbookId);
  if (!tb) return null;
  const meta = tb.unitIndex.find(u => u.id === unitId || String(u.no) === String(unitId));
  if (!meta?.file) return null;
  const raw = await fetchJSON(`${BASE}/${tb.dir}/${meta.file}`);
  return normalizeUnitContent(raw, textbookId, unitId);
}

function normalizeUnitContent(raw, textbookId, unitId) {
  const uid = raw.id || unitId;
  const grammar = (raw.grammar || []).map((g, i) => ({
    id: `f-${textbookId}-${uid}-g${i}`,
    textbookId, unitId: uid, status: 'confirmed', source: 'file', ...g,
  }));
  const questions = attachQuestionRefs(
    (raw.questions || []).map((q, i) => ({
      id: `f-${textbookId}-${uid}-q${i}`,
      textbookId, unitId: uid, status: 'confirmed', origin: 'textbook', source: 'file', ...q,
    })),
    grammar,
    { fallbackPrefix: `f-${textbookId}-${uid}-q` },
  );
  return {
    unit: {
      id: uid,
      textbookId,
      no: Number(raw.no) || 0,
      title: raw.title || '',
      theme: raw.theme || '',
      objectives: raw.objectives || [],
    },
    words: (raw.words || []).map((w, i) => ({
      id: `f-${textbookId}-${uid}-w${i}`,
      textbookId, unitId: uid, status: 'confirmed', source: 'file', ...w,
    })),
    sentences: (raw.sentences || []).map((s, i) => ({
      id: `f-${textbookId}-${uid}-s${i}`,
      textbookId, unitId: uid, status: 'confirmed', source: 'file', ...s,
    })),
    grammar,
    questions,
    texts: raw.texts || [],
    phrases: raw.phrases || [],
    phonics: raw.phonics || null,
  };
}

/** 从存储读取（导入的）内容 */
async function loadUnitFromStore(textbookId, unitId) {
  const storage = getStorage();
  const [words, sentences, grammar, questions, units] = await Promise.all([
    storage.query(COLLECTIONS.WORDS, 'unitId', unitId),
    storage.query(COLLECTIONS.SENTENCES, 'unitId', unitId),
    storage.query(COLLECTIONS.GRAMMAR, 'unitId', unitId),
    storage.query(COLLECTIONS.QUESTIONS, 'unitId', unitId),
    storage.query(COLLECTIONS.UNITS, 'textbookId', textbookId),
  ]);
  const unit = units.find(u => u.id === unitId);
  const confirmed = list => list.filter(x => x.status === 'confirmed');
  return {
    unit: unit || null,
    words: confirmed(words),
    sentences: confirmed(sentences),
    grammar: confirmed(grammar),
    questions: confirmed(questions),
  };
}

/**
 * 获取某单元全部内容（合并 文件 + 存储，按去重键优先保留存储版本）
 * @returns {Promise<{unit, words, sentences, grammar, questions}>}
 */
export async function getUnitContent(textbookId, unitId) {
  const key = `${textbookId}::${unitId}`;
  if (cache.unitContent.has(key)) return cache.unitContent.get(key);

  const [fromFile, fromStore] = await Promise.all([
    loadUnitFromFile(textbookId, unitId).catch(() => null),
    loadUnitFromStore(textbookId, unitId),
  ]);

  const merged = {
    unit: fromStore.unit || fromFile?.unit || null,
    words: mergeByKey(fromFile?.words || [], fromStore.words || [], w => w.en.toLowerCase()),
    sentences: mergeByKey(fromFile?.sentences || [], fromStore.sentences || [], s => s.en.toLowerCase()),
    grammar: mergeByKey(fromFile?.grammar || [], fromStore.grammar || [], g => `${g.category}|${g.rule}`),
    questions: mergeByKey(fromFile?.questions || [], fromStore.questions || [],
      q => `${q.type}|${String(q.prompt).trim()}`),
    // 课文 / 词组 / 语音：文件版优先（这三类目前在导入中心尚无本地编辑入口），
    // 存储版将来若带上同名字段则覆盖文件版
    texts: fromStore.texts ?? fromFile?.texts ?? [],
    phrases: fromStore.phrases ?? fromFile?.phrases ?? [],
    phonics: fromStore.phonics ?? fromFile?.phonics ?? null,
  };
  cache.unitContent.set(key, merged);
  return merged;
}

/**
 * 按 key 合并两组条目，override 覆盖 base 里同 key 条目的**已有字段**，
 * 但 override 里**缺失的字段**保留 base 里的（文件优先提供完整结构，存储只改用户改过的字段）。
 * override 里 base 没有的新 key 条目会被整体加入（新增内容）。
 */
function mergeByKey(base, override, keyFn) {
  const map = new Map();
  base.forEach(x => map.set(keyFn(x), x));
  override.forEach(x => {
    const k = keyFn(x);
    const existing = map.get(k);
    if (existing) {
      // 字段级 merge：override 显式提供的非空字段优先；空字符串/undefined 保留 base
      const merged = { ...existing };
      for (const [fk, fv] of Object.entries(x)) {
        if (fv === undefined || fv === null) continue;
        if (typeof fv === 'string' && fv === '') continue;  // 空字符串不覆盖 base 的有效值
        merged[fk] = fv;
      }
      // 但 override 里如果 status 显式给了 confirmed，那就要覆盖（存储确认过）
      if (x.status) merged.status = x.status;
      map.set(k, merged);
    } else {
      map.set(k, x);
    }
  });
  return [...map.values()];
}

/** 列出某教材的单元（合并文件索引 + 存储中新增的单元） */
export async function listUnits(textbookId) {
  const tb = cache.textbooks.get(textbookId);
  const storage = getStorage();
  const storedUnits = await storage.query(COLLECTIONS.UNITS, 'textbookId', textbookId);
  const map = new Map();
  (tb?.unitIndex || []).forEach(u => map.set(u.id, {
    id: u.id, textbookId, no: u.no, title: u.title,
    theme: '', objectives: [], source: 'file',
  }));
  storedUnits.forEach(u => map.set(u.id, { ...u, source: 'store' }));
  return [...map.values()].sort((a, b) => a.no - b.no);
}

/** 列出全部教材（文件 + 存储中新建的） */
export async function listTextbooks() {
  await loadCatalog();
  const storage = getStorage();
  const stored = await storage.list(COLLECTIONS.TEXTBOOKS);
  const map = new Map();
  [...cache.textbooks.values()].forEach(t => map.set(t.id, t));
  stored.forEach(t => map.set(t.id, {
    ...t, source: 'store',
    unitIndex: [...(map.get(t.id)?.unitIndex || [])],
  }));
  return [...map.values()].sort((a, b) => (a.grade - b.grade) || a.name.localeCompare(b.name));
}

/** 内容变更后清缓存（导入/编辑/删除时调用） */
export function invalidateCache(textbookId, unitId) {
  if (!textbookId) { cache.unitContent.clear(); return; }
  if (!unitId) {
    for (const k of [...cache.unitContent.keys()]) {
      if (k.startsWith(`${textbookId}::`)) cache.unitContent.delete(k);
    }
    return;
  }
  cache.unitContent.delete(`${textbookId}::${unitId}`);
}

export function resetDataSourceCache() {
  cache.catalogLoaded = false;
  cache.textbooks.clear();
  cache.unitContent.clear();
}

/** 内容统计（不触发全量加载，用于教材列表页） */
export async function unitStats(textbookId, unitId) {
  const c = await getUnitContent(textbookId, unitId);
  return {
    words: c.words.length,
    sentences: c.sentences.length,
    texts: c.texts?.length || 0,
    grammar: c.grammar.length,
    questions: c.questions.length,
    textbookQuestions: c.questions.filter(q => q.origin === 'textbook').length,
  };
}
